import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link2, Search, Plus, Loader2, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { tr, uiLocale } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

function formatError(detail) {
  if (!detail) return tr("Something went wrong. Please try again.");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

// Insert an inline <a> immediately before the closing </p> of the LAST <p> in
// body_html, or append a linking sentence at the end if no <p> was found.
function insertInternalLink(bodyHtml, { title, url }) {
  const anchor = `<a href="${url}">${title}</a>`;
  if (!bodyHtml) return `<p>Related: ${anchor}.</p>`;
  // Avoid dup insertion
  if (bodyHtml.includes(url)) return bodyHtml;
  const lastClose = bodyHtml.lastIndexOf("</p>");
  if (lastClose === -1) return `${bodyHtml}\n<p>Related: ${anchor}.</p>`;
  const injection = ` <em>Related: ${anchor}.</em>`;
  return (
    bodyHtml.slice(0, lastClose) + injection + bodyHtml.slice(lastClose)
  );
}

export default function InternalLinksPanel({ article }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [insertingId, setInsertingId] = useState(null);

  const used = article.internal_links_used || [];
  const usedIds = new Set(used.map((u) => u.id));

  const doSearch = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const res = await api.wpArticlesSearch(q.trim(), 15);
      setResults(res.results || []);
      if ((res.results || []).length === 0) {
        toast.info(tr("No matches — try broader keywords"));
      }
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || e.message);
    } finally {
      setSearching(false);
    }
  };

  const insertMutation = useMutation({
    mutationFn: async (candidate) => {
      const newBody = insertInternalLink(article.body_html, candidate);
      return api.updateArticle(article.id, { body_html: newBody });
    },
    onSuccess: () => {
      toast.success(tr("Internal link inserted"));
      qc.invalidateQueries({ queryKey: ["article", article.id] });
      setInsertingId(null);
    },
    onError: (e) => {
      toast.error(formatError(e?.response?.data?.detail) || e.message);
      setInsertingId(null);
    },
  });

  const onInsert = (candidate) => {
    setInsertingId(candidate.id);
    insertMutation.mutate(candidate);
  };

  return (
    <section
      data-testid="internal-links-panel"
      className="mb-12 bg-white border border-[var(--border)]"
    >
      <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-2">
        <Link2 className="w-3.5 h-3.5 text-[var(--accent)]" />
        {trRich("<0>Internal links</0><1>· {usedCount} used</1>", { usedCount: used.length }, [<span className="overline" />, <span className="text-xs text-[var(--ink-muted)] ml-1" />])}
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Used */}
        {used.length > 0 ? (
          <ul className="space-y-1.5">
            {used.map((u) => (
              <li
                key={u.id}
                data-testid={`internal-link-used-${u.id}`}
                className="flex items-start gap-2 text-sm"
              >
                <span className="text-[var(--ink-muted)] mt-0.5">→</span>
                <a
                  href={u.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--accent)] hover:text-[var(--accent-hover)] underline underline-offset-2 flex-1 break-words"
                >
                  {u.title}
                </a>
                <ExternalLink className="w-3 h-3 text-[var(--ink-muted)] mt-1 shrink-0" />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-[var(--ink-muted)] italic">{tr("No internal links yet. Use the search below to add one manually, or regenerate the article after syncing your WP archive in Settings.")}</p>
        )}

        {/* Manual search */}
        <div className="border-t border-[var(--border)] pt-4">
          <div className="text-xs text-[var(--ink-muted)] mb-2">{tr("Search your WordPress archive to add another link:")}</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
              <input
                data-testid="internal-links-search-input"
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    doSearch();
                  }
                }}
                placeholder={tr("e.g. Baja 1000 winner, expat healthcare, border wait times…")}
                className="w-full border border-[var(--border)] pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[var(--slate-900)]"
              />
            </div>
            <button
              data-testid="internal-links-search-btn"
              onClick={doSearch}
              disabled={searching || !q.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-50"
            >
              {searching ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Search className="w-3.5 h-3.5" />
              )}{tr("Search")}</button>
            {results.length > 0 && (
              <button
                onClick={() => {
                  setResults([]);
                  setQ("");
                }}
                className="inline-flex items-center gap-1 px-2 text-xs text-[var(--ink-muted)] hover:text-[var(--ink)]"
                title={tr("Clear results")}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {results.length > 0 && (
            <ul
              data-testid="internal-links-search-results"
              className="mt-3 divide-y divide-[var(--border)] border border-[var(--border)]"
            >
              {results.map((r) => {
                const alreadyUsed = usedIds.has(r.id);
                return (
                  <li
                    key={r.id}
                    className="flex items-start gap-2 px-3 py-2 hover:bg-[var(--secondary)]"
                  >
                    <div className="flex-1 min-w-0">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-medium text-[var(--ink)] hover:text-[var(--accent)] block break-words"
                      >
                        {r.title}
                      </a>
                      {r.excerpt && (
                        <div className="text-xs text-[var(--ink-muted)] mt-0.5 line-clamp-2">
                          {r.excerpt}
                        </div>
                      )}
                      <div className="text-[10px] text-[var(--ink-muted)] font-mono-display mt-0.5">{tr("score {score}", { score: r.score })}
                        {r.published_at && (
                          <>
                            {" · "}
                            {new Date(r.published_at).toLocaleDateString(uiLocale())}
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      data-testid={`internal-links-insert-${r.id}`}
                      onClick={() => onInsert(r)}
                      disabled={
                        alreadyUsed ||
                        insertingId === r.id ||
                        insertMutation.isPending
                      }
                      className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        alreadyUsed
                          ? tr("Already linked in this article")
                          : tr("Insert as an internal link into the article body")
                      }
                    >
                      {insertingId === r.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Plus className="w-3 h-3" />
                      )}
                      {alreadyUsed ? tr("Used") : tr("Insert")}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
